import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAppConfig } from 'src/api/pmApi';
import { getUser } from 'src/api/stoApi';
import { useAsset } from 'src/context-providers/AssetContext';
import { BRICKWISE_APP_FRONTEND_URL } from 'src/util/config';
import { awaitUtil } from 'src/util/utils';
import { Check } from '@mui/icons-material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import { Button, CircularProgress } from '@mui/material';
import Bowser from 'bowser';

export const CopyBrickwiseLinkButton = () => {
  const { asset } = useAsset();
  const [copiedLink, setCopiedLink] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const { t } = useTranslation();

  const generateLink = async () => {
    setIsGeneratingLink(true);
    const [response] = await awaitUtil(getUser());
    const partnerId = response?.data?.data?.partnerId;
    let domainUriPrefix = BRICKWISE_APP_FRONTEND_URL;
    if (partnerId) {
      const [appConfig] = await awaitUtil(getAppConfig(partnerId));
      const partnerDomain = appConfig?.data?.data?.appURL;
      if (partnerDomain) {
        domainUriPrefix = partnerDomain;
      }
    }
    const link = `${domainUriPrefix}/desktopWeb/asset/${asset.id}/details`;
    const mobileDevicesRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
    // if desktop copy the link to clipboard, if mobile phone open the link in a new tab
    if (mobileDevicesRegex.test(navigator.userAgent)) {
      window.open(link, '_blank');
      throw new Error('Mobile device');
    } else {
      return link;
    }
  };

  const afterClipboard = () => {
    setIsGeneratingLink(false);
    setCopiedLink(true);
    setCopiedToFalseAfterTimeout();
  };

  const copyLink = async () => {
    const browser = Bowser.getParser(window.navigator.userAgent);
    try {
      // Safari does not support navigator.clipboard.writeText
      if (browser.getBrowserName() === 'Safari') {
        const clipboardItem = new ClipboardItem({
          'text/plain': generateLink().then((link) => {
            return new Blob([link], { type: 'text/plain' });
          }),
        });
        if (true) {
          navigator.clipboard.write([clipboardItem]);
        }
        return;
      }
      const link = await generateLink();
      if (link !== '') {
        await navigator.clipboard.writeText(link);
      }
    } catch (e) {
      console.log(e);
    } finally {
      afterClipboard();
    }
  };

  const determinLinkIcon = () => {
    if (copiedLink) {
      return <Check />;
    }
    if (isGeneratingLink) {
      return <CircularProgress size="1rem" />;
    }
    return <PhoneIphoneIcon />;
  };

  const determinButtonText = () => {
    if (copiedLink) {
      return t('dashboard.assets.addedit.main.copied');
    }
    if (isGeneratingLink) {
      return t('dashboard.assets.addedit.main.loading');
    }
    return t('dashboard.assets.addedit.main.preview');
  };

  const setCopiedToFalseAfterTimeout = () => {
    setTimeout(() => {
      setCopiedLink(false);
    }, 2000);
  };

  return (
    <Button variant="contained" disableElevation color="inherit" startIcon={determinLinkIcon()} onClick={copyLink}>
      {determinButtonText()}
    </Button>
  );
};
